import { useState } from "react";
import QRCode from "qrcode";
import { useQueryClient } from "@tanstack/react-query";
import { authClient } from "../../lib/auth-client";
import { useAuth } from "../../app/auth-context";
import { Button, Card, Field, Input, PageTitle } from "../../components/ui";

// 二要素認証（TOTP）のエンロール（F-11・[19] §6.7）。
// admin は必須（未登録だと保護ページに入れず本ページへ誘導される）。
export function SecurityPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [password, setPassword] = useState("");
  const [qr, setQr] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function startEnroll(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await authClient.twoFactor.enable({ password });
      if (res.error || !res.data) {
        setError("パスワードが正しくありません。");
        return;
      }
      const { totpURI, backupCodes } = res.data as {
        totpURI: string;
        backupCodes: string[];
      };
      setQr(await QRCode.toDataURL(totpURI));
      setBackupCodes(backupCodes ?? []);
    } catch {
      setError("設定の開始に失敗しました。");
    } finally {
      setLoading(false);
    }
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await authClient.twoFactor.verifyTotp({ code });
      if (res.error) {
        setError("認証コードが正しくありません。");
        return;
      }
      await qc.invalidateQueries({ queryKey: ["me"] });
    } catch {
      setError("認証に失敗しました。");
    } finally {
      setLoading(false);
    }
  }

  if (user?.twoFactorEnabled) {
    return (
      <div className="mx-auto max-w-xl">
        <PageTitle title="セキュリティ設定" />
        <Card className="text-center">
          <p className="text-2xl">🔐</p>
          <p className="mt-2 text-sm font-bold text-emerald-600">二要素認証（TOTP）は有効です。</p>
          <p className="mt-1 text-xs text-slate-500">
            ログイン時に認証アプリのコードが必要になります。
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <PageTitle
        title="二要素認証の設定"
        subtitle={
          user?.role === "admin"
            ? "管理者アカウントは二要素認証の設定が必須です。"
            : "アカウントの安全のため、二要素認証をおすすめします。"
        }
      />
      {!qr ? (
        <Card>
          <form className="space-y-4" onSubmit={startEnroll}>
            <p className="text-sm text-slate-600">
              認証アプリ（Google Authenticator 等）と連携します。まずパスワードで本人確認します。
            </p>
            <Field label="パスワード">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Field>
            {error && <p className="text-sm font-bold text-rose-500">{error}</p>}
            <Button type="submit" disabled={loading}>
              設定を開始
            </Button>
          </form>
        </Card>
      ) : (
        <Card className="space-y-4">
          <div className="text-center">
            <p className="text-sm font-bold text-slate-600">
              認証アプリでこのQRコードを読み取ってください
            </p>
            <img src={qr} alt="TOTP QRコード" className="mx-auto mt-3 h-48 w-48" />
          </div>
          {backupCodes.length > 0 && (
            <div className="rounded-soft bg-sky-soft p-4">
              <p className="text-xs font-bold text-slate-600">
                バックアップコード（安全な場所に保管してください）
              </p>
              <div className="mt-2 grid grid-cols-2 gap-1 font-mono text-xs text-slate-700">
                {backupCodes.map((b) => (
                  <span key={b}>{b}</span>
                ))}
              </div>
            </div>
          )}
          <form className="space-y-3" onSubmit={verify}>
            <Field label="認証アプリに表示された6桁コード">
              <Input
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
            </Field>
            {error && <p className="text-sm font-bold text-rose-500">{error}</p>}
            <Button type="submit" disabled={loading}>
              有効化する
            </Button>
          </form>
        </Card>
      )}
    </div>
  );
}
